<template>
  <div class="page-container-v2">
    <a-spin :spinning="loading" tip="加载中...">
      <page-header
        :title="$t('certificatelist_management')"
        :sub-title="$t('certificatelist_management_desc')"
      >
      </page-header>

      <div class="card-body px-0 pt-0 pb-2 table-with-search">
        <!-- 통계 섹션 — 필터 패널보다 위, 첫 번째 섹션. 실패(백엔드 미배포 포함) 시 스스로
             아무것도 렌더링하지 않으므로 화면 흐름에 영향이 없다. -->
        <cert-stats-section :manufacturer-id="manufacturerId" />

        <!-- 검색 필터 영역 — certificate-card 페이지(batches/issue)와 동일한 CertCardFilterPanel 룩앤필 -->
        <cert-card-filter-panel @search="enter" @reset="handleReset">
          <!-- group1: 검색조건 + 검색어 -->
          <template #group1>
            <a-select
              v-model:value="searchFilter"
              style="width: 150px"
              :placeholder="$t('common_searchfilter_select')"
              @change="handleSearchFilterChange"
            >
              <a-select-option
                v-for="option in searchFilterOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </a-select-option>
            </a-select>
            <a-input
              v-model:value="condition"
              style="width: 280px"
              allow-clear
              :placeholder="getSearchPlaceholder"
              @pressEnter="enter"
            />
          </template>

          <!-- group2: 제조사(XINLAIAN 전용) + 조회 날짜 -->
          <template #group2>
            <!-- 제조사 필터 — 플랫폼 운영자(XINLAIAN) 전용. jwtRole()은 UI 노출 게이트일 뿐
                 보안 경계가 아니다 — 실제 차단은 china-inapi가 TenantContext에서 파라미터를
                 무시(fail-closed)하는 것으로 수행한다(SPEC-CHINA-AUTHZ-PLATFORM-SCOPE-001). -->
            <template v-if="isXinlaian">
              <span class="cert-filter-label">{{ $t('certificatelist_manufacturer') }}</span>
              <a-select
                v-model:value="manufacturerId"
                style="width: 200px"
                show-search
                allow-clear
                :placeholder="$t('certificatelist_manufacturer_filter_all')"
                :options="manufacturerOptions"
                :filter-option="filterManufacturerOption"
                :loading="manufacturerOptionsLoading"
                @change="handleManufacturerChange"
              />
            </template>
            <span class="cert-filter-label" :style="isXinlaian ? 'margin-left: 8px;' : ''">
              {{ $t('certificatelist_get_date') }}
            </span>
            <a-range-picker
              v-model:value="dateRange.dates"
              :format="'YYYY-MM-DD'"
              :disabled-date="disabledDate"
              @change="handleDateRangeChange"
              :show-time="false"
              :disabled="isSearching"
              style="width: 240px;"
              :locale="antDateLocale"
            />
            <!-- 백엔드는 검색어가 있으면 날짜 범위를 무시한다. 기간이 함께 적용된다고
                 오해하지 않도록 비활성화하고 이유를 밝힌다. -->
            <span v-if="isSearching" class="date-ignored-hint">
              {{ $t('certificatelist_date_ignored_hint') }}
            </span>
          </template>
        </cert-card-filter-panel>

        <!-- 테이블 정보 영역 -->

        <div class="col-xs-12 table-info-filter">
          <div class="total-div">
            <p>
              {{ $t('common_total_count') }} <span class="number-highlight">{{ formatCommaNumber(listSize) }}</span> {{ $t('common_total_count_item') }}
            </p>
          </div>
          <div class="search-container">
            <a-select 
              style="width: 150px"
              v-model:value="rowsPerPage" 
              @change="handleRowsPerPage"
              size="small"
            >
              <a-select-option :value="10">{{ $t('common_rows_per_page_10') }}</a-select-option>
              <a-select-option :value="20">{{ $t('common_rows_per_page_20') }}</a-select-option>
              <a-select-option :value="50">{{ $t('common_rows_per_page_50') }}</a-select-option>
              <a-select-option :value="100">{{ $t('common_rows_per_page_100') }}</a-select-option>
            </a-select>
          </div>
        </div>

        <!-- 이름 퀵필터 안내 배너 — 동명이인 가능성 + 날짜 범위 무시 안내(component-local 상태, URL 미저장) -->
        <a-alert
          v-if="nameQuickFilterActive"
          type="info"
          closable
          show-icon
          :message="$t('certificatelist_quickfilter_name_banner')"
          class="quickfilter-name-banner"
          @close="nameQuickFilterActive = false"
        />

        <!-- 테이블 영역 -->
        <div v-if="!isMobileView">
          <a-table
            class="ant-table-striped"
            size="small"
            :columns="visibleColumns"
            :data-source="responseData"
            :pagination="false"
            :scroll="{ x: 1100 }"
            v-if="responseData.length > 0"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'action'">
                <div class="cert-action-buttons">
                  <a-button
                    type="link"
                    size="small"
                    @click="showCertificateDetail(record)"
                  >
                    {{ $t('common_view_detail') }}
                  </a-button>
                  <!-- customerMobile 이 없으면 팔로워를 조회할 방법이 없어 버튼을 비활성화하고 이유를 툴팁으로 밝힌다 -->
                  <a-tooltip :title="record.customerMobile ? '' : $t('certificatelist_follower_button_disabled_tip')">
                    <a-button
                      type="link"
                      size="small"
                      :disabled="!record.customerMobile"
                      @click="openFollowerLookup(record)"
                    >
                      {{ $t('certificatelist_follower_button') }}
                    </a-button>
                  </a-tooltip>
                </div>
              </template>
            </template>
          </a-table>
          <div class="no-result-container" v-else>
            <i class="fa-solid fa-exclamation-triangle" />
            <span>{{ $t('certificatelist_no_result') }}</span>
            <span class="no-result-hint">{{ noResultHint }}</span>
          </div>
        </div>

        <!-- 모바일 카드뷰 -->
        <div v-else class="mobile-card-list">
          <div v-for="(record, idx) in responseData" :key="record._id || idx" class="mobile-company-card">
            <div class="card-field">
              <span class="card-label">{{ $t('certificatelist_customer_name') }}:</span>
              <span>{{ record.customerName || '-' }}</span>
              <!-- 모바일은 hover 가 없으므로 아이콘을 항상 노출하되 저채도로, 44x44 탭 타겟으로 분리한다 -->
              <button
                v-if="record.customerName"
                type="button"
                class="quickfilter-mobile-btn"
                :aria-label="$t('certificatelist_quickfilter_tooltip')"
                @click.stop="applyQuickFilter('customerName', record.customerName)"
              >
                <FilterOutlined />
              </button>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('certificatelist_customer_mobile') }}:</span>
              <span>{{ formatPhoneNumber(record.customerMobile) }}</span>
              <button
                v-if="record.customerMobile"
                type="button"
                class="quickfilter-mobile-btn"
                :aria-label="$t('certificatelist_quickfilter_tooltip')"
                @click.stop="applyQuickFilter('customerMobile', record.customerMobile)"
              >
                <FilterOutlined />
              </button>
            </div>
            <!-- 데스크탑 테이블과 동일하게 제조사를 품목 이름 앞에 배치 -->
            <div class="card-field" v-if="record.manufacturerName || record.manufacturerId">
              <span class="card-label">{{ $t('certificatelist_manufacturer') }}:</span>
              <span v-if="record.manufacturerName">{{ record.manufacturerName }}</span>
              <span v-else class="manufacturer-id-fallback">{{ record.manufacturerId }}</span>
              <a-tooltip v-if="record.manufacturerId" :title="$t('common_copy')">
                <button
                  type="button"
                  class="manufacturer-copy-btn"
                  @click.stop="copyManufacturerId(record.manufacturerId)"
                >
                  <CopyOutlined />
                </button>
              </a-tooltip>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('certificatelist_item_name') }}:</span>
              <span>{{ record.itemName || '-' }}</span>
            </div>
            <div class="card-field" v-if="record.productCode">
              <span class="card-label">{{ $t('certificatelist_serial_no') }}:</span>
              <span>{{ record.productCode }}</span>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('certificatelist_issued_at') }}:</span>
              <span>{{ formatDate(record.createAt) }}</span>
            </div>
            <div class="card-field">
              <span class="card-label">{{ $t('certificatelist_issue_status') }}<issue-status-help />:</span>
              <span>{{ formatIssueStatus(record.issueStatus) }}</span>
            </div>
            <div class="card-actions">
              <a-button size="small" @click="showCertificateDetail(record)">
                {{ $t('common_view_detail') }}
              </a-button>
              <a-button v-if="record.productCode" size="small" @click="goToProductsList(record)">
                {{ $t('certificatelist_view_product') }}
              </a-button>
              <a-tooltip :title="record.customerMobile ? '' : $t('certificatelist_follower_button_disabled_tip')">
                <a-button size="small" :disabled="!record.customerMobile" @click="openFollowerLookup(record)">
                  {{ $t('certificatelist_follower_button') }}
                </a-button>
              </a-tooltip>
            </div>
          </div>
          <div v-if="!loading && responseData.length === 0" class="no-result-container">
            <i class="fa-solid fa-exclamation-triangle" />
            <span>{{ $t('certificatelist_no_result') }}</span>
            <span class="no-result-hint">{{ noResultHint }}</span>
          </div>
        </div>
      </div>

      <!-- 페이지네이션 -->
      <pagination 
        v-if="!loading && responseData.length > 0"
        :page-info="paginationData"
        page-name="CertificateList"
        @change-page="paginationHandler"
      />
    </a-spin>

    <!-- 상세보기 모달 추가 -->
    <certificate-detail-modal
      v-model:visible="detailModalVisible"
      :certificate-id="selectedCertificateId"
      @refresh="fetchCertificateList"
      @update:visible="onModalVisibilityChange"
    />

    <!-- 팔로워 조회 Drawer — 화면 이탈 없이 customerMobile 로 팔로워 정보를 확인 -->
    <FollowerLookupDrawer
      v-model:visible="followerDrawerVisible"
      :mobile="followerDrawerMobile"
      :customer-name="followerDrawerCustomerName"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import { oneBtnDialog, formatCommaNumber, jwtRole } from '@/utils/utils';
import { getCompanyOptions } from '@/apis/chinaCompany';
import PageHeader from '@/views/components/PageHeader.vue';
import Pagination from '@/views/components/Pagination.vue';
import certificateApis from './api';
import { hasAuthCodeKey } from './authCodeColumn';
import chinaInapi from '@/apis/chinaInapi';
import CertificateDetailModal from './components/CertificateDetailModal.vue';
import CertStatsSection from './components/CertStatsSection.vue';
import IssueStatusHelp from './components/IssueStatusHelp.vue';
import CertCardFilterPanel from '@/views/CertificateCard/components/CertCardFilterPanel.vue';
import FollowerLookupDrawer from './components/FollowerLookupDrawer.vue';
import { antDateLocaleMixin } from '@/mixins/antDateLocaleMixin';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import { CopyOutlined, FilterOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';

export default {
  mixins: [antDateLocaleMixin, dealerEcountListMixin],
  name: 'CertificateList',
  components: {
    PageHeader,
    Pagination,
    CertificateDetailModal,
    CertStatsSection,
    IssueStatusHelp,
    CertCardFilterPanel,
    FollowerLookupDrawer,
    CopyOutlined,
    FilterOutlined
  },
  data() {
    return {
      formatCommaNumber,
      apis: null,
      loading: false,
      responseData: [],
      listSize: 0,
      rowsPerPage: 10,
      rowsPerPageOptions: [10, 20, 50, 100],
      searchFilter: 'customerName',
      condition: '',
      // 셀 클릭 퀵필터가 환자 이름으로 진입했는지 여부 — 동명이인 안내 배너 표시 조건.
      // URL 쿼리에는 싣지 않는다(재방문 시 배너 재현 불필요, component-local 상태로만 관리)
      nameQuickFilterActive: false,
      // 제조사 필터 (XINLAIAN 전용) — null이면 전체 조회(파라미터 미전송)
      manufacturerId: null,
      manufacturerOptions: [],
      manufacturerOptionsLoading: false,
      searchFilterOptions: [
        { value: 'customerName', label: this.$t('certificatelist_customer_name') },
        { value: 'customerMobile', label: this.$t('certificatelist_customer_mobile') },
        { value: 'serialNo', label: this.$t('certificatelist_serial_no') },
      ],
      dateRange: {
        dates: [dayjs().subtract(3, 'month'), dayjs()],
        startDate: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD')
      },
      maxDateRange: 730,
      paginationData: {
        currentPage: 1,
        totalPages: 0
      },
      columns: [
        {
          title: this.$t('certificatelist_customer_name'),
          dataIndex: 'customerName',
          key: 'customerName',
          align: 'center',
          // 셀 클릭 퀵필터 — 제조사 컬럼의 복사 아이콘과 동일한 인라인 아이콘 패턴
          customRender: ({text, record}) => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              <span>{text || '-'}</span>
              {record.customerName && (
                <a-tooltip title={this.$t('certificatelist_quickfilter_tooltip')}>
                  <button
                    type="button"
                    class="quickfilter-btn"
                    onClick={(e) => { e.stopPropagation(); this.applyQuickFilter('customerName', record.customerName); }}
                  >
                    <FilterOutlined />
                  </button>
                </a-tooltip>
              )}
            </div>
          )
        },
        {
          title: this.$t('certificatelist_customer_mobile'),
          dataIndex: 'customerMobile',
          key: 'customerMobile',
          align: 'center',
          customRender: ({text, record}) => (
            <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
              <span>{this.formatPhoneNumber(text)}</span>
              {record.customerMobile && (
                <a-tooltip title={this.$t('certificatelist_quickfilter_tooltip')}>
                  <button
                    type="button"
                    class="quickfilter-btn"
                    onClick={(e) => { e.stopPropagation(); this.applyQuickFilter('customerMobile', record.customerMobile); }}
                  >
                    <FilterOutlined />
                  </button>
                </a-tooltip>
              )}
            </div>
          )
        },
        // 제조사 컬럼은 품목 이름 왼쪽에 배치한다 (2026-08-20 운영 요청 — 소속 제조사를
        // 먼저 식별한 뒤 품목을 읽는 운영자 탐색 순서에 맞춤)
        {
          title: this.$t('certificatelist_manufacturer'),
          dataIndex: 'manufacturerName',
          key: 'manufacturer',
          align: 'center',
          customRender: ({record}) => {
            const { manufacturerId, manufacturerName } = record;
            if (!manufacturerName && !manufacturerId) return '-';
            return (
              <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
                {manufacturerName
                  ? <span>{manufacturerName}</span>
                  : <span class="manufacturer-id-fallback">{manufacturerId}</span>}
                {manufacturerId && (
                  <a-tooltip title={this.$t('common_copy')}>
                    <button
                      type="button"
                      class="manufacturer-copy-btn"
                      onClick={(e) => { e.stopPropagation(); this.copyManufacturerId(manufacturerId); }}
                    >
                      <CopyOutlined />
                    </button>
                  </a-tooltip>
                )}
              </div>
            );
          }
        },
        {
          title: this.$t('certificatelist_item_name'),
          dataIndex: 'itemName',
          key: 'itemName',
          align: 'center'
        },
        {
          title: this.$t('certificatelist_serial_no'),
          dataIndex: 'productCode',
          key: 'serialNo',
          align: 'center',
          customRender: ({text, record}) => {
            if (text) {
              return (
                <div style="display: flex; flex-direction: column; align-items: center;">
                  <span>{text}</span>
                  <a-button
                    type="default"
                    size="small"
                    style="margin-top: 4px; padding: 0 8px; height: 24px !important; font-size: 12px; white-space: nowrap;"
                    onClick={() => this.goToProductsList(record)}
                  >
                    {this.$t('certificatelist_view_product')}
                  </a-button>
                </div>
              );
            }
            return '-';
          }
        },
        {
          // 써마지 인증코드(고객 입력값) — 노출은 visibleColumns 게이트(운영자 또는 써마지 응답)로 결정
          title: this.$t('certificatelist_auth_code'),
          dataIndex: 'authCode',
          key: 'authCode',
          align: 'center',
          customRender: ({text}) => text || '-'
        },
        {
          title: this.$t('certificatelist_surgery_area'),
          dataIndex: ['surgeryArea', 'value'],
          key: 'surgeryArea',
          align: 'center'
        },
        {
          title: this.$t('certificatelist_surgery_date'),
          dataIndex: 'surgeryAt',
          key: 'surgeryAt',
          align: 'center',
          customRender: ({text}) => this.formatDateOnly(text)
        },
        {
          title: this.$t('certificatelist_issued_at'),
          dataIndex: 'createAt',
          key: 'createAt',
          align: 'center',
          customRender: ({text}) => this.formatDate(text)
        },
        {
          title: () => (
            <div style="display: flex; align-items: center; justify-content: center;">
              {this.$t('certificatelist_issue_status')}
              {/* JSX 에서는 kebab 태그가 해석되지 않는다 — 임포트 참조로 렌더링 */}
              <IssueStatusHelp />
            </div>
          ),
          dataIndex: 'issueStatus',
          key: 'issueStatus',
          align: 'center',
          customRender: ({text}) => this.formatIssueStatus(text)
        },
        {
          title: this.$t('certificatelist_action'),
          key: 'action',
          align: 'center',
          // 상세 보기 + 팔로워 보기 2버튼 수용을 위해 100 -> 160으로 확장
          width: 160
        }
      ],
      detailModalVisible: false,
      selectedCertificateId: '',
      // 팔로워 조회 Drawer 상태 — 선택된 행의 customerMobile/customerName 만 보관(재조회는 Drawer 책임)
      followerDrawerVisible: false,
      followerDrawerMobile: '',
      followerDrawerCustomerName: ''
    };
  },
  computed: {
    // 플랫폼 운영자(XINLAIAN) 여부 — UI 노출 게이트 전용(보안 경계 아님, utils.js jwtRole 주석 참조).
    // 제조사 계정이 파라미터를 위조 전송해도 백엔드가 TenantContext 스코프로 무시한다.
    isXinlaian() {
      return jwtRole() === 'XINLAIAN';
    },

    // 인증코드 칸 노출 게이트(UI 전용) — 운영자(XINLAIAN)이거나 응답 행에 써마지 인증코드 키가 있을 때만.
    // china-inapi 가 써마지 문서에만 authCode 키를 싣으므로 덴티움 계정 화면엔 칸이 생기지 않는다(2026-10-08).
    visibleColumns() {
      const show = this.isXinlaian || hasAuthCodeKey(this.responseData);
      return show ? this.columns : this.columns.filter((c) => c.key !== 'authCode');
    },

    getSearchPlaceholder() {
      // itemName 은 검색조건 목록에도 백엔드에도 없다(china-inapi 에서 의도적 미지원) — 제외
      const placeholders = {
        customerName: this.$t('certificatelist_customer_name_placeholder'),
        customerMobile: this.$t('certificatelist_customer_mobile_placeholder'),
        serialNo: this.$t('certificatelist_serial_no_placeholder')
      };
      return placeholders[this.searchFilter] || this.$t('certificatelist_search_placeholder');
    },

    // 검색어가 있으면 백엔드가 날짜 범위를 무시한다(china-inapi findAdminList)
    isSearching() {
      return !!(this.condition || '').trim();
    },

    // 0건일 때 어떤 조건 때문인지 알려준다
    noResultHint() {
      return this.isSearching
        ? this.$t('certificatelist_no_result_search_hint')
        : this.$t('certificatelist_no_result_date_hint');
    }
  },
  // created 와 mounted 가 각각 조회해 진입 시 목록 API 가 두 번 호출되던 것을 한 번으로 합쳤다
  async created() {
    // SPEC-CHINA-ADMIN-CERT-FOLLOWER-EP-001 M3: 한국 inapi($apiInstance) → china-inapi 재지향
    this.apis = certificateApis(chinaInapi);

    // URL 쿼리로 진입 시 검색 상태 복원
    // 예: /china/certificate/list?searchFilter=serialNo&condition=124C612449&page=2&limit=50
    const { searchFilter, condition, page, limit, manufacturerId } = this.$route.query;
    if (searchFilter) this.searchFilter = searchFilter;
    if (condition) this.condition = condition;
    if (Number(page) > 0) this.paginationData.currentPage = Number(page);
    if (this.rowsPerPageOptions.includes(Number(limit))) this.rowsPerPage = Number(limit);
    // 제조사 필터 복원은 운영자에게만 의미가 있다 — 제조사 계정은 백엔드가 무시하므로
    // URL에 남아 있어도 화면 상태로 끌어오지 않는다(필터 UI 자체가 없어 해제 불가능해짐 방지)
    if (manufacturerId && this.isXinlaian) this.manufacturerId = manufacturerId;

    // 옵션 로드는 목록 조회와 독립 — 실패해도 목록은 정상 동작(필터만 빈 옵션)
    if (this.isXinlaian) this.loadManufacturerOptions();

    await this.getCertificateList();
  },
  methods: {
    // 검색어 정규화 — 목록은 전화번호를 formatPhoneNumber 로 가공해(189-8402-6862) 보여주지만
    // DB 는 구분자 없는 11자리로 저장한다(운영 실측: deleteYn='N' 4,392건 전부 구분자 없음).
    // 백엔드가 입력값 그대로 정규식 부분일치를 걸기 때문에, 화면에 보이는 대로 붙여넣으면
    // 반드시 0건이 된다. 전송 직전에 구분자를 제거해 표시 형식과 검색 형식을 일치시킨다.
    normalizeCondition(searchFilter, raw) {
      const trimmed = (raw || '').trim();
      // 전화번호: 숫자 외 전부 제거 (하이픈·공백·점·괄호)
      if (searchFilter === 'customerMobile') return trimmed.replace(/\D/g, '');
      // 시리얼번호: 영숫자 혼합이므로 구분자만 제거 (124HU13322)
      if (searchFilter === 'serialNo') return trimmed.replace(/[\s-]/g, '');
      // 이름: 앞뒤 공백만 제거 (중간 공백은 실제 이름의 일부일 수 있음)
      return trimmed;
    },

    // 인증서 목록 조회
    async getCertificateList() {
      try {
        this.loading = true;
        const params = {
          page: this.paginationData.currentPage,
          limit: this.rowsPerPage
        };

        const searchTerm = this.normalizeCondition(this.searchFilter, this.condition);

        // 검색어가 있는 경우에는 검색 관련 파라미터만 전송
        if (searchTerm) {
          params.search = searchTerm;
          params.searchFilter = this.searchFilter;
        }
        // 검색어가 없는 경우에만 날짜 범위 전송
        else if (this.dateRange.startDate && this.dateRange.endDate) {
          params.startDate = this.dateRange.startDate;
          params.endDate = this.dateRange.endDate;
        }

        // 제조사 필터는 검색어·날짜 어느 모드와도 함께 적용된다(백엔드 양 분기 모두 지원)
        if (this.manufacturerId) {
          params.manufacturerId = this.manufacturerId;
        }

        const response = await this.apis.getCertificateList(params);

        if (response.data) {
          const { items, paginator } = response.data;
          
          if (items && Array.isArray(items)) {
            this.responseData = [...items];
            this.listSize = paginator?.totalItems || 0;
            this.paginationData = {
              currentPage: paginator?.currentPage || 1,
              totalPages: paginator?.totalPages || 0
            };
          } else {
            this.responseData = [];
            this.listSize = 0;
            this.paginationData.totalPages = 0;
          }
        } else {
          this.responseData = [];
          this.listSize = 0;
          this.paginationData.totalPages = 0;
          await oneBtnDialog(
            this.$t('certificatelist_search_fail'),
            response.data?.message || this.$t('certificatelist_search_fail_content'),
            false
          );
        }
      } catch (error) {
        // 기존 코드는 catch 안에서 스코프에 없는 `response` 를 참조해, 통신 오류가 나면
        // ReferenceError 로 바뀌면서 원인이 가려지고 안내 다이얼로그도 뜨지 않았다.
        console.error('Error fetching certificate list:', error);
        this.responseData = [];
        this.listSize = 0;
        this.paginationData.totalPages = 0;
        await oneBtnDialog(
          this.$t('certificatelist_search_fail'),
          error?.response?.data?.message || this.$t('certificatelist_search_fail_content'),
          false
        );
      } finally {
        this.loading = false;
        this.syncQueryToUrl();
      }
    },

    // 검색 상태를 URL 에 반영 — 제품 목록으로 이동했다가 뒤로 오면 조건이 유지된다.
    // replace 로 히스토리를 늘리지 않으며, 라우터의 중복 내비게이션 거부는 무시한다.
    syncQueryToUrl() {
      const query = {};
      const searchTerm = this.normalizeCondition(this.searchFilter, this.condition);
      if (searchTerm) {
        query.searchFilter = this.searchFilter;
        query.condition = searchTerm;
      }
      if (this.manufacturerId) query.manufacturerId = this.manufacturerId;
      if (this.paginationData.currentPage > 1) query.page = String(this.paginationData.currentPage);
      if (this.rowsPerPage !== 10) query.limit = String(this.rowsPerPage);

      const current = this.$route.query;
      const same =
        Object.keys(query).length === Object.keys(current).length &&
        Object.keys(query).every((k) => String(current[k]) === query[k]);
      if (same) return;

      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },

    // 제조사 필터 옵션 로드 — XINLAIAN 전용 EP(GET /china/company-options?type=MANUFACTURER).
    // CompanyFormModal.vue와 동일 소스. 실패는 무시(빈 옵션) — 목록 조회를 막지 않는다.
    async loadManufacturerOptions() {
      try {
        this.manufacturerOptionsLoading = true;
        const response = await getCompanyOptions('MANUFACTURER');
        this.manufacturerOptions = (response.data || []).map((c) => ({
          value: c._id,
          label: c.name,
        }));
      } catch (e) {
        console.error('Error loading manufacturer options:', e);
        this.manufacturerOptions = [];
      } finally {
        this.manufacturerOptionsLoading = false;
      }
    },

    // 제조사 필터 변경(선택·해제 공통) — 1페이지부터 재조회
    async handleManufacturerChange() {
      this.paginationData.currentPage = 1;
      await this.getCertificateList();
    },

    // a-select show-search 매칭 — 제조사명 부분일치(대소문자 무시)
    filterManufacturerOption(input, option) {
      return (option.label || '').toLowerCase().includes((input || '').toLowerCase());
    },

    // 검색조건(항목) 변경 — 이전 항목용으로 입력한 값이 남아 엉뚱하게 조회되는 것을 막는다
    async handleSearchFilterChange() {
      // 검색 select 를 수동으로 바꿨으므로 퀵필터에서 온 이름 배너는 더 이상 유효하지 않다
      this.nameQuickFilterActive = false;
      if (!this.condition) return;
      this.condition = '';
      this.paginationData.currentPage = 1;
      await this.getCertificateList();
    },

    // 셀 클릭 퀵필터 — 환자 이름/휴대폰 번호 셀 아이콘 클릭 시 원클릭으로 그 값을 검색조건으로 적용
    async applyQuickFilter(type, value) {
      if (!value) return;
      this.searchFilter = type;
      this.condition = value;
      // 이름으로 진입했을 때만 동명이인 안내 배너를 띄운다. 전화번호 퀵필터는 배너를 끈다.
      this.nameQuickFilterActive = (type === 'customerName');
      this.paginationData.currentPage = 1;
      await this.getCertificateList();
    },

    // 날짜 범위 변경 핸들러
    async handleDateRangeChange(dates) {
      if (!dates) {
        this.dateRange = {
          dates: null,
          startDate: null,
          endDate: null
        };
      } else {
        const [start, end] = dates;
        
        // 날짜 범위 체크 (2년)
        const diffDays = end.diff(start, 'day');
        if (diffDays > this.maxDateRange) {
          await oneBtnDialog(
            this.$t('certificatelist_date_range_exceed'),
            this.$t('certificatelist_date_range_limit'),
            false
          );
          return;
        }

        this.dateRange = {
          dates,
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD')
        };
      }
      
      this.paginationData.currentPage = 1;
      await this.getCertificateList();
    },

    // 날짜 선택 제한
    disabledDate(current) {
      return current && current > dayjs().endOf('day');
    },

    // 검색 버튼 클릭 또는 엔터 입력 시
    async enter() {
      // 사용자가 직접 검색을 실행했으므로 퀵필터에서 온 이름 배너는 더 이상 유효하지 않다
      this.nameQuickFilterActive = false;
      // 실제로 검색에 쓰인 값을 입력창에도 반영해, 무엇으로 조회됐는지 보이게 한다
      this.condition = this.normalizeCondition(this.searchFilter, this.condition);
      this.paginationData.currentPage = 1;
      await this.getCertificateList();
    },

    // 페이지당 행 수 변경
    async handleRowsPerPage() {
      this.paginationData.currentPage = 1;
      await this.getCertificateList();
    },

    // 페이지 변경
    async paginationHandler(childPassParam) {
      if (!childPassParam || !childPassParam.page) return;
      this.paginationData.currentPage = childPassParam.page;
      await this.getCertificateList();
    },

    // 검색 조건 초기화
    handleReset() {
      this.searchFilter = 'customerName';
      this.condition = '';
      this.nameQuickFilterActive = false;
      this.manufacturerId = null;
      this.dateRange = {
        dates: [dayjs().subtract(3, 'month'), dayjs()],
        startDate: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD')
      };
      this.paginationData.currentPage = 1;
      this.getCertificateList();
    },

    // 제품 목록 페이지로 이동
    goToProductsList(record) {
      // 제품 목록 페이지로 이동하고 시리얼 번호로 검색
      this.$router.push({
        path: '/china/product-list',
        query: {
          searchFilter: 'serialNo',
          condition: record.productCode
        }
      });
    },

    // 인증서 상세 정보 조회
    async showCertificateDetail(record) {
      this.selectedCertificateId = record._id;
      this.detailModalVisible = true;
    },

    // 팔로워 조회 Drawer 오픈 — customerMobile 없으면 버튼 자체가 비활성화되어 여기 도달하지 않는다
    openFollowerLookup(record) {
      if (!record.customerMobile) return;
      this.followerDrawerMobile = record.customerMobile;
      this.followerDrawerCustomerName = record.customerName || '';
      this.followerDrawerVisible = true;
    },

    // 제조사 ID 클립보드 복사 — navigator.clipboard 는 보안 컨텍스트(HTTPS/localhost)에서만
    // 동작하므로 실패 시 execCommand 폴백을 사용한다 (Geo/components/CopyableId.vue 와 동일 패턴)
    async copyManufacturerId(id) {
      if (!id) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(id);
        } else {
          this.execCommandCopyFallback(id);
        }
        message.success(this.$t('common_copied'));
      } catch (e) {
        try {
          this.execCommandCopyFallback(id);
          message.success(this.$t('common_copied'));
        } catch (e2) {
          message.error(this.$t('common_copy_failed'));
        }
      }
    },

    execCommandCopyFallback(text) {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    },

    // 전화번호 포맷팅
    formatPhoneNumber(phone) {
      if (!phone) return '-';
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    },

    // 날짜 포맷팅
    // 발급 시각 — DB 는 UTC 로 저장, 화면은 중국시간(UTC+8) 으로 보여준다. 종전 dayjs.utc().format 은
    // UTC 그대로 출력해 통계 섹션("중국시간(UTC+8) 기준")과 같은 화면에서 8시간 어긋났다(2026-09-03 운영 실측).
    formatDate(date) {
      if (!date) return '-';
      return dayjs.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm');
    },

    // 수술일 — 날짜 전용 값(입력 raw '2026/09/03' → UTC 자정으로 저장). 시각을 붙이면 UTC 로는 00:00,
    // UTC+8 로는 08:00 이 돼 둘 다 오해를 부르므로 저장된 달력 날짜만 그대로 보여준다.
    formatDateOnly(date) {
      if (!date) return '-';
      return dayjs.utc(date).format('YYYY-MM-DD');
    },

    // 위챗 알림 발송 상태 포맷팅 (china-inapi IssueStatus 6단계: 발급 자체는 모든 상태에서 완료된 상태)
    formatIssueStatus(status) {
      const statusMap = {
        'PENDING': this.$t('certificatelist_issue_status_pending'),
        'SAVED': this.$t('certificatelist_issue_status_saved'),
        'COUNTED': this.$t('certificatelist_issue_status_counted'),
        'NOTIFIED': this.$t('certificatelist_issue_status_notified'),
        'NOTIFY_FAILED': this.$t('certificatelist_issue_status_notify_failed'),
        'SUPPRESSED': this.$t('certificatelist_issue_status_suppressed')
      };
      return statusMap[status] || status;
    },

    // 인증서 목록 조회 함수
    async fetchCertificateList() {
      console.log('fetchCertificateList called');
      await this.getCertificateList();
    },

    onModalVisibilityChange(visible) {
      if (!visible) {
        // 모달이 닫힐 때 강제로 목록 재조회
        console.log('Modal closed, refreshing list');
        setTimeout(() => {
          this.getCertificateList();
        }, 100);
      }
    }
  },
  watch: {
    detailModalVisible(newVal) {
      if (!newVal) {
        // 모달이 닫힐 때 목록 재조회
        this.fetchCertificateList();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* 필터 인라인 레이블 — CertCardFilterPanel 사용 페이지(batches/issue) 공통 관례 */
.cert-filter-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

/* 액션 컬럼 — 상세 보기 + 팔로워 보기 2버튼, 160px 폭에서 줄바꿈 허용 */
.cert-action-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 4px;
}

@media screen and (max-width: 767px) {
  .mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
  .mobile-company-card { border: 1px solid #f0f0f0; border-radius: 6px; padding: 12px; background: #fff; }
  .card-field { display: flex; gap: 8px; margin-bottom: 6px; font-size: 13px; align-items: center; }
  .card-label { font-weight: 600; min-width: 90px; flex-shrink: 0; color: #666; }
  .card-actions { display: flex; flex-wrap: wrap; gap: 6px; border-top: 1px solid #f5f5f5; padding-top: 8px; }
  .card-actions :deep(.ant-btn) { flex: 1 1 auto; }
}

.table-info-filter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  .total-div {
    p {
      margin: 0;
      font-size: 14px;
      
      .number-highlight {
        color: #1890ff;
        font-weight: bold;
        margin: 0 4px;
      }
    }
  }
}

.no-result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #999;

  i {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .no-result-hint {
    margin-top: 4px;
    font-size: 12px;
    color: #bbb;
  }
}

.date-ignored-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 32px;
}

@media screen and (max-width: 767px) {
  .date-ignored-hint {
    margin-left: 0;
    line-height: 1.5;
  }
}

.manufacturer-id-fallback {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #999;
}

.manufacturer-copy-btn {
  border: none;
  background: transparent;
  padding: 0 2px;
  cursor: pointer;
  color: #bfbfbf;
  line-height: 1;
  font-size: 12px;
}

.manufacturer-copy-btn:hover {
  color: #1677ff;
}

/* 셀 클릭 퀵필터 아이콘 — 제조사 복사 아이콘과 동일한 시각적 무게 */
.quickfilter-btn {
  border: none;
  background: transparent;
  padding: 0 2px;
  cursor: pointer;
  color: #bfbfbf;
  line-height: 1;
  font-size: 12px;
}

.quickfilter-btn:hover {
  color: #1677ff;
}

.quickfilter-name-banner {
  margin-bottom: 12px;
}

/* 모바일 카드뷰 — hover 가 없으므로 항상 노출, 저채도, 44x44 탭 타겟으로 텍스트와 분리 */
.quickfilter-mobile-btn {
  border: none;
  background: transparent;
  color: #bfbfbf;
  padding: 0;
  margin-left: 4px;
  width: 44px;
  height: 44px;
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

.quickfilter-mobile-btn:active {
  color: #1677ff;
}

:deep(.ant-table-striped) {
  .ant-table-tbody {
    tr:nth-child(odd) {
      background-color: #fafafa;
    }
    
    tr:hover {
      background-color: #e6f7ff !important;
    }
  }
}
</style>
